import React, { createRef, useState, RefObject, useContext, useEffect } from 'react';
import { AccountType, DetailAccountInfo, AccountInfoByType, AccountInfo } from './Types';
import { getAjaxAccountInfo } from './apis';

export type AccountContextProps = {
  isLoading: boolean,
  accounts: AccountInfoByType[];
  selectedAccount: DetailAccountInfo | undefined;
  setSelectedAccount: (args: DetailAccountInfo | undefined) => void;
  hasNestedAccounts: boolean;
  selectedAccountRef: RefObject<HTMLElement>;
  setSelectedAccountRef: (args: RefObject<HTMLElement>) => void;
  selectedAccountType: AccountType | undefined;
  setSelectedAccountType: (args: AccountType | undefined) => void;
  filteredAccounts: AccountInfoByType[];
  setFilteredAccounts: (args: AccountInfoByType[]) => void;
  preSelectedAccount: DetailAccountInfo | undefined;
  setPreSelectedAccount: (args: DetailAccountInfo | undefined) => void;
  numberOfAccountSelection: number;
  setNumberOfAccountSelection: (args: number) => void;
}

const AccountContext = React.createContext<AccountContextProps>({
  isLoading: false,
  accounts: [],
  selectedAccount: undefined,
  setSelectedAccount: () => {},
  hasNestedAccounts: false,
  selectedAccountRef: createRef<HTMLElement>(),
  setSelectedAccountRef: () => {},
  selectedAccountType: undefined,
  setSelectedAccountType: () => {},
  filteredAccounts: [],
  setFilteredAccounts: () => {},
  preSelectedAccount: undefined,
  setPreSelectedAccount: () => {},
  numberOfAccountSelection: 0,
  setNumberOfAccountSelection: () => {}
});

export const useAccountContext = () => {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error(
      'AccountContext must be used within a AccountContextProvider',
    );
  }
  return context;
}

export const getPreSelectedAccountType = (
    accounts: AccountInfoByType[],
    entityId: string
): AccountType | undefined => {
  // Iterate over each AccountInfoByType in accounts
  for (const accountType of accounts) {
    // Iterate over each DetailAccountInfo in AccountInfoBytype
    for (const account of accountType.accounts) {
      // Check the nestedAccounts property if it contains the preSelected entityId
      const hasNestedAccountPreSelected = account.nestedAccounts.some(
          (nestedAccount: DetailAccountInfo) => {
            return nestedAccount.entityId === entityId;
          }
      );
      // Return early if we've matched the entityId correctly
      if (account.entityId === entityId || hasNestedAccountPreSelected) {
        return accountType.accountType;
      }
    }
  }

  // exhausted account list and did not find account.
  return undefined
};

const getPreSelectedAccount = (accounts: AccountInfoByType[], entityId): DetailAccountInfo | undefined => {
  let preSelectedAccount: DetailAccountInfo | undefined = undefined;

  // Check pre-selected account against query param globalAccountId
  const params = new URLSearchParams(window.location.search);
  const globalAccountId = params.get('globalAccountId');
  if (globalAccountId && globalAccountId.length > 0) {
    const hasPreSelectedGlobalAccount = accounts.some((accountType: AccountInfoByType) => {
      return accountType.accounts?.some((account: DetailAccountInfo) => {
        if (account.entityId === globalAccountId) {
          preSelectedAccount = account;
          return true;
        }
      });
    });

    if (hasPreSelectedGlobalAccount) {
      return preSelectedAccount;
    }
  }

  // Check pre-selected account against current entityId in accountInfo
  accounts.some((accountType: AccountInfoByType) => {
    accountType.accounts?.some((account: DetailAccountInfo) => {
      const hasNestedAccountPreSelected = account.nestedAccounts.some((nestedAccount: DetailAccountInfo) => {
        return nestedAccount.entityId === entityId;
      })
      if (hasNestedAccountPreSelected || account.entityId === entityId) {
        preSelectedAccount = account;
        return true;
      }
    });
  });

  return preSelectedAccount;
}

const shouldRenderCountrySelector = (accounts: AccountInfoByType[]) => {
  return accounts.some((account: AccountInfoByType) => {
    return account.accounts.some((account: DetailAccountInfo) => {
      return account.nestedAccounts.length > 0;
    });
  });
};

export const AccountContextProvider: React.FC = ({ children }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [accounts, setAccounts] = useState<AccountInfoByType[]>([]);
  const [selectedAccountType, setSelectedAccountType] = useState<AccountType>();
  const [selectedAccountRef, setSelectedAccountRef] = useState<RefObject<HTMLElement>>(createRef());
  const [filteredAccounts, setFilteredAccounts] = useState<AccountInfoByType[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<DetailAccountInfo>();
  const [preSelectedAccount, setPreSelectedAccount] = useState<DetailAccountInfo | undefined>(getPreSelectedAccount([], undefined));
  const [numberOfAccountSelection, setNumberOfAccountSelection] = useState<number>(0);

  const hasNestedAccounts = shouldRenderCountrySelector(accounts);

  useEffect(() => {
    const setAjaxAccountInfo = async () => {
      const {accounts, currentEntityId} = (await getAjaxAccountInfo()) as AccountInfo;
      setIsLoading(false);
      setAccounts(accounts);
      setFilteredAccounts(accounts);
      setPreSelectedAccount(getPreSelectedAccount(accounts, currentEntityId));
      setSelectedAccountType(getPreSelectedAccountType(accounts, currentEntityId))
    };

    setAjaxAccountInfo();
  }, []);

  const value = {
    isLoading,
    accounts,
    selectedAccount,
    setSelectedAccount,
    selectedAccountType,
    setSelectedAccountType,
    hasNestedAccounts,
    selectedAccountRef,
    setSelectedAccountRef,
    filteredAccounts,
    setFilteredAccounts,
    preSelectedAccount,
    setPreSelectedAccount,
    numberOfAccountSelection,
    setNumberOfAccountSelection
  };

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  )
}
